import { endOfDay, format, startOfDay, startOfYear, subDays } from "date-fns";

import { BillingCycleOption } from "./billingCycleOptions";

export type CoachVideoProjectDatePreset =
  | "last_30_days"
  | "last_60_days"
  | "last_90_days"
  | "ytd"
  | "billing_period"
  | "custom";

export const COACH_VIDEO_PROJECT_DATE_PRESET_LABELS: Record<CoachVideoProjectDatePreset, string> = {
  last_30_days: "Last 30 Days",
  last_60_days: "Last 60 Days",
  last_90_days: "Last 90 Days",
  ytd: "Year to Date",
  billing_period: "Billing Period",
  custom: "Custom Range",
};

export const COACH_VIDEO_PROJECT_DATE_PRESETS = Object.keys(
  COACH_VIDEO_PROJECT_DATE_PRESET_LABELS
) as CoachVideoProjectDatePreset[];

export const COACH_VIDEO_PROJECT_DATE_PRESET_STORAGE_KEY = "coachVideoProjects-datePreset";

interface StoredCoachVideoProjectDateFilter {
  preset: CoachVideoProjectDatePreset | null;
  startDate: string | null;
  endDate: string | null;
}

function isCoachVideoProjectDatePreset(value: string): value is CoachVideoProjectDatePreset {
  return value in COACH_VIDEO_PROJECT_DATE_PRESET_LABELS;
}

export function readStoredCoachVideoProjectDateFilter(): StoredCoachVideoProjectDateFilter {
  const empty = { preset: null, startDate: null, endDate: null };
  if (typeof localStorage === "undefined") return empty;

  const stored = localStorage.getItem(COACH_VIDEO_PROJECT_DATE_PRESET_STORAGE_KEY);
  if (!stored) return empty;
  if (isCoachVideoProjectDatePreset(stored)) {
    return { preset: stored, startDate: null, endDate: null };
  }

  try {
    const parsed = JSON.parse(stored) as { preset?: unknown; startDate?: unknown; endDate?: unknown };
    if (typeof parsed.preset !== "string" || !isCoachVideoProjectDatePreset(parsed.preset)) return empty;
    return {
      preset: parsed.preset,
      startDate: typeof parsed.startDate === "string" ? parsed.startDate : null,
      endDate: typeof parsed.endDate === "string" ? parsed.endDate : null,
    };
  } catch {
    return empty;
  }
}

export function writeStoredCoachVideoProjectDateFilter(
  preset: CoachVideoProjectDatePreset | null,
  startDate: string | null = null,
  endDate: string | null = null
): void {
  if (typeof localStorage === "undefined") return;
  if (!preset) {
    localStorage.removeItem(COACH_VIDEO_PROJECT_DATE_PRESET_STORAGE_KEY);
    return;
  }
  if (preset === "custom") {
    localStorage.setItem(COACH_VIDEO_PROJECT_DATE_PRESET_STORAGE_KEY, JSON.stringify({ preset, startDate, endDate }));
    return;
  }
  localStorage.setItem(COACH_VIDEO_PROJECT_DATE_PRESET_STORAGE_KEY, preset);
}

export function initialCoachVideoProjectDateFilter(now: Date = new Date()): StoredCoachVideoProjectDateFilter {
  const stored = readStoredCoachVideoProjectDateFilter();
  if (!stored.preset) return stored;
  const dates = resolveCoachVideoProjectDatePreset(stored.preset, stored, now);
  return { preset: stored.preset, ...dates };
}

const ROLLING_DAY_COUNTS: Partial<Record<CoachVideoProjectDatePreset, number>> = {
  last_30_days: 30,
  last_60_days: 60,
  last_90_days: 90,
};

export interface CreatedDateBounds {
  createdAfter?: string;
  createdBefore?: string;
}

export function resolveCoachVideoProjectDatePreset(
  preset: CoachVideoProjectDatePreset,
  current?: { startDate: string | null; endDate: string | null },
  now: Date = new Date()
): { startDate: string | null; endDate: string | null } {
  const rollingDays = ROLLING_DAY_COUNTS[preset];
  if (rollingDays) {
    return {
      startDate: format(subDays(now, rollingDays), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  if (preset === "ytd") {
    return {
      startDate: format(startOfYear(now), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  if (preset === "custom") {
    if (current?.startDate || current?.endDate) {
      return { startDate: current.startDate ?? null, endDate: current.endDate ?? null };
    }
    return {
      startDate: format(subDays(now, 30), "yyyy-MM-dd"),
      endDate: format(now, "yyyy-MM-dd"),
    };
  }

  return { startDate: null, endDate: null };
}

function parseLocalDate(date: string): Date {
  return new Date(`${date}T00:00:00`);
}

export function createdDateBoundsFromRange(startDate: string | null, endDate: string | null): CreatedDateBounds {
  return {
    ...(startDate ? { createdAfter: startOfDay(parseLocalDate(startDate)).toISOString() } : {}),
    ...(endDate ? { createdBefore: endOfDay(parseLocalDate(endDate)).toISOString() } : {}),
  };
}

export function createdDateBoundsFromCycle(cycle: BillingCycleOption): CreatedDateBounds {
  return {
    createdAfter: startOfDay(cycle.start).toISOString(),
    createdBefore: endOfDay(cycle.end).toISOString(),
  };
}
